import { sharedLib } from "@shared/services/shared.service";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DisplayResultMessage,
  OpenMessage,
  PlanUsageLimits,
  RequestSource,
  SubscriptionPlanType,
  UsageLimitValue,
  UsageType,
} from "@shared/types";
import pluralize from "pluralize";
import { isNumber } from "lodash";
import {
  USAGE_TYPE_TO_INLINE_OBJECT_NAME,
  USAGE_TYPE_TO_TITLE_OBJECT_NAME,
} from "@shared/features/settings/constants";
import {
  useClientsData,
  useUser,
  useUserUsageTypes,
} from "@shared/utils/hooks/misc";
import { checkPassedQuota } from "@shared/features/settings/utils";
import { useTheme } from "styled-components";
import moment from "moment";
import { getSubscriptionTypeDisplay } from "@shared/utils/misc";
import { SHOW_DELETE_USER_BUTTON_FEATURE_FLAG } from "@shared/common/constants";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { skipToken } from "@reduxjs/toolkit/query";

interface useSharedQuotaModalProps {
  usageType?: UsageType;
  onTriggerClick?: () => void;
  checkImmediately?: boolean;
  onModalClose?: () => void;
  displayResultMessage?: DisplayResultMessage;
}
export const useSharedQuotaModal = ({
  usageType,
  onTriggerClick,
  checkImmediately,
  onModalClose,
  displayResultMessage,
}: useSharedQuotaModalProps) => {
  const {
    isLoading: isLoadingUsageLimits,
    userUsageLimits,
    getNextPlanData: getNextPlan,
    getTotalQuota,
    getExtraQuota,
  } = useUserUsageLimits();
  const [getAllQuotaUsage, getAllQuotaUsageStatus] =
    sharedLib.sharedApi.settings.endpoints.getAllQuotaUsage.useLazyQuery();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingQuotaUsage, setIsLoadingQuotaUsage] = useState(false);
  const { user } = useUser(true);
  const usageTypes = useUserUsageTypes();

  const baseUsageLimit = usageType ? userUsageLimits?.[usageType] : undefined;
  const totalUsageLimit = usageType
    ? getTotalQuota(usageType, userUsageLimits?.[usageType])
    : undefined;
  const allQuotaUsage = getAllQuotaUsageStatus.data;
  const currentUsage = (usageType && allQuotaUsage?.[usageType]) ?? 0;
  const isLoading = isLoadingUsageLimits || isLoadingQuotaUsage;
  const isSubscriptionExpired = user?.isExpired();
  const nextPlanData = useMemo(
    () => getNextPlan(currentUsage, usageType),
    [currentUsage, usageType]
  );
  const triggerClickDataRef = useRef<{
    onTriggerClick?: () => void;
  }>();
  triggerClickDataRef.current = {
    onTriggerClick,
  };

  const handleTriggerClick = useCallback(
    async (args?: {
      numItems?: number;
      closeModal?: () => void;
      allowDuringGrace?: boolean;
      ignoreQuotaCheck?: boolean;
    }): Promise<boolean | undefined> => {
      const {
        numItems = 1,
        closeModal,
        allowDuringGrace,
        ignoreQuotaCheck,
      } = args || {};
      const handleOpenModal = () => {
        if (closeModal) {
          new Promise<void>((resolve) => {
            closeModal();
            setTimeout(() => resolve(), 400);
          }).then(() => setIsOpen(true));
        } else {
          setIsOpen(true);
        }
      };

      if (
        isSubscriptionExpired &&
        (!allowDuringGrace || !user?.in_view_only_grace_period)
      ) {
        handleOpenModal();
        return Promise.resolve(true);
      }
      if (!usageType || ignoreQuotaCheck) {
        triggerClickDataRef.current?.onTriggerClick?.();
        return Promise.resolve(false);
      }
      setIsLoadingQuotaUsage(true);
      return displayResultMessage?.(getAllQuotaUsage().unwrap())
        .then((response?: PlanUsageLimits) => {
          const currentUsage = response?.[usageType];
          const didPassQuota =
            currentUsage !== undefined &&
            !!totalUsageLimit &&
            typeof currentUsage === "number" &&
            typeof totalUsageLimit === "number" &&
            currentUsage + numItems > totalUsageLimit;
          if (didPassQuota) {
            handleOpenModal();
          } else {
            triggerClickDataRef.current?.onTriggerClick?.();
          }
          return Promise.resolve(didPassQuota);
        })
        .finally(() => setIsLoadingQuotaUsage(false));
    },
    [user, totalUsageLimit, usageType]
  );

  useEffect(() => {
    if (checkImmediately) {
      handleTriggerClick().catch(() => {});
    }
  }, [checkImmediately]);

  const getSortedUsageTypes = () => {
    const getUsageDifference = (usageType: UsageType) => {
      const currentUsage = allQuotaUsage?.[usageType];
      let usageLimit = getTotalQuota(usageType, userUsageLimits?.[usageType]);
      if (currentUsage === "inf") return -1;
      else if (usageLimit === "inf") usageLimit = 999999999;
      if (typeof currentUsage === "number" && typeof usageLimit === "number") {
        return currentUsage / usageLimit;
      } else if (typeof currentUsage === "number") return 1;
      else if (typeof usageLimit === "number") return -1;
      return 0;
    };

    return usageTypes.slice().sort((a, b) => {
      return getUsageDifference(b) - getUsageDifference(a);
    });
  };

  const getNextPlanData = () => {
    if (!nextPlanData) return;
    const sortedUsageTypes = getSortedUsageTypes();
    return {
      name: getSubscriptionTypeDisplay(nextPlanData.type),
      limits: sortedUsageTypes.slice(0, 3).map((usageType) => {
        const limit = nextPlanData.usageLimits?.[usageType];
        const baseLimit = limit === "inf" ? "Unlimited" : limit;
        return {
          baseLimit,
          extraLimit: getExtraQuota(usageType),
          objectName: USAGE_TYPE_TO_TITLE_OBJECT_NAME[usageType],
        };
      }),
    };
  };

  const pluralizedObjectName = usageType
    ? pluralize(
        USAGE_TYPE_TO_INLINE_OBJECT_NAME[usageType] || "",
        isNumber(totalUsageLimit) ? totalUsageLimit : 2
      )
    : undefined;

  const currentUsageObjectText = `${totalUsageLimit} ${pluralizedObjectName}`;

  const getTitle = () => {
    if (isSubscriptionExpired) return "Subscription expired";
    if (isMaxPlanClientQuota)
      return "You have reached your Primary API Slots limit";
    return "You have reached your plan's limit";
  };

  const getOkText = () => {
    if (isSubscriptionExpired) return "Renew Subscription";
    if (isMaxPlanClientQuota) return "Purchase More";
  };

  const isMaxPlan = !!user?.isPro();
  const isMaxPlanClientQuota = isMaxPlan && usageType === UsageType.CLIENT;
  const currentUsageName = isMaxPlanClientQuota
    ? "Primary API Slots"
    : "Current Usage";

  const onCancel = useCallback(() => {
    setIsOpen(false);
    onModalClose?.();
  }, []);

  return {
    handleTriggerClick,
    isOpen,
    onCancel,
    title: getTitle(),
    currentUsageObjectText,
    isLoading,
    currentUsage,
    usageLimit: baseUsageLimit,
    nextPlanData: getNextPlanData(),
    usageType,
    currentUsageName,
    isMaxPlanClientQuota,
    isSubscriptionExpired,
    okText: getOkText(),
    isMaxPlan,
  };
};

export const useQuotaTest = () => {
  const { getTotalQuota, getExtraQuota } = useUsageLimits();

  const testQuota = (
    usageType: UsageType,
    usage?: UsageLimitValue,
    limit?: UsageLimitValue,
    graceRatio?: number
  ) => {
    const totalQuota = getTotalQuota(usageType, limit);
    return checkPassedQuota(usage, totalQuota, graceRatio);
  };

  return {
    testQuota,
    getExtraQuota,
    getTotalQuota,
  };
};

export const useUsageLimits = (refetchOnMountOrArgChange?: boolean) => {
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { data: usageLimitsData, ...getUsageLimitsStatus } =
    sharedLib.sharedApi.settings.useGetUsageLimitsQuery(
      isExternalConnectAccount ? skipToken : undefined,
      {
        refetchOnMountOrArgChange,
      }
    );
  const usageLimits = usageLimitsData?.limits;
  const additionalLimits = usageLimitsData?.additional_limits;

  const getUsageLimits = (planType: SubscriptionPlanType) =>
    usageLimits?.[planType];

  const usageTypeToExtraLimits = useMemo(() => {
    return additionalLimits
      ?.filter((limit) => limit.expiration_timestamp > moment().unix())
      .reduce((acc: { [key in UsageType]?: number }, usageLimit) => {
        const usageType = usageLimit.usage_type;
        const quota = usageLimit.quota;
        const prevValue = acc[usageType];
        if (prevValue) {
          acc[usageType] = prevValue + quota;
        } else {
          acc[usageType] = quota;
        }
        return acc;
      }, {});
  }, [additionalLimits]);

  const getExtraQuota = (usageType: UsageType) => {
    return usageTypeToExtraLimits?.[usageType];
  };

  const getTotalQuota = (usageType: UsageType, limit?: UsageLimitValue) => {
    const extraQuota = getExtraQuota(usageType);
    return typeof limit === "number" && extraQuota ? limit + extraQuota : limit;
  };

  return {
    usageLimits,
    getUsageLimits,
    getExtraQuota,
    getTotalQuota,
    getUsageLimitsStatus,
  };
};

export const useUserUsageLimits = (refetchOnMountOrArgChange?: boolean) => {
  const {
    usageLimits,
    getUsageLimits,
    getUsageLimitsStatus,
    getTotalQuota,
    getExtraQuota,
  } = useUsageLimits(refetchOnMountOrArgChange);
  const { user } = useUser();

  const userSubscriptionPlan = user?.subscription_plan_type;
  const userUsageLimits = userSubscriptionPlan
    ? getUsageLimits(userSubscriptionPlan)
    : undefined;
  const allPlans = Object.values(
    SubscriptionPlanType
  ) as SubscriptionPlanType[];

  const getNextPlanType = (
    planIndex: number,
    currentUsage: UsageLimitValue,
    usageType: UsageType
  ) => {
    return (
      allPlans.slice(planIndex + 1).find((plan) => {
        const planUsageLimit = getUsageLimits(plan)?.[usageType];
        if (planUsageLimit && planUsageLimit > currentUsage) {
          return plan;
        }
      }) || allPlans[planIndex + 1]
    );
  };

  const getNextPlanData = (
    currentUsage: UsageLimitValue,
    usageType?: UsageType
  ) => {
    if (!userSubscriptionPlan || user?.isPro()) return;
    const currentPlanIndex = allPlans.indexOf(userSubscriptionPlan);
    const nextPlanType = usageType
      ? getNextPlanType(currentPlanIndex, currentUsage, usageType)
      : allPlans[currentPlanIndex + 1];

    return {
      type: nextPlanType,
      usageLimits: getUsageLimits(nextPlanType),
    };
  };

  return {
    userUsageLimits,
    usageLimits,
    isLoading: getUsageLimitsStatus.isLoading,
    getNextPlanData,
    getTotalQuota,
    getExtraQuota,
  };
};

export const useCheckQuotaUsage = (
  checkImmediately?: boolean,
  planType?: SubscriptionPlanType
) => {
  const { data: usageLimitsData } =
    sharedLib.sharedApi.settings.useGetUsageLimitsQuery();
  const [getAllQuotaUsage, getAllQuotaUsageStatus] =
    sharedLib.sharedApi.settings.endpoints.getAllQuotaUsage.useLazyQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [passedQuota, setPassedQuota] = useState(false);
  const usageLimits = usageLimitsData?.limits;
  const { testQuota, getExtraQuota } = useQuotaTest();
  const usageTypes = useUserUsageTypes();

  const checkQuotaUsage = (planType?: SubscriptionPlanType) => {
    if (!planType || !usageLimits) return;

    const planUsageLimits = usageLimits[planType];
    setIsLoading(true);
    return getAllQuotaUsage()
      .unwrap()
      .then((quotaUsage) => {
        const hasPassedQuota = usageTypes.some((usageType) => {
          return (
            usageType !== UsageType.BACKTESTING &&
            testQuota(
              usageType,
              quotaUsage?.[usageType],
              planUsageLimits?.[usageType],
              usageType === UsageType.ACTIVE_TRADE ? 0.3 : undefined
            )
          );
        });
        setPassedQuota(hasPassedQuota);
        return Promise.resolve(hasPassedQuota);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (checkImmediately && usageLimits && planType) checkQuotaUsage(planType);
  }, [checkImmediately, !!usageLimits, planType]);

  return {
    checkQuotaUsage,
    isLoading,
    allQuotaUsage: getAllQuotaUsageStatus.data,
    passedQuota,
    getExtraQuota,
  };
};

export const useInvalidAccount = () => {
  const theme = useTheme();
  const { getIsAccountInvalid, getIsAccountInactive } = useClientsData();

  const getInvalidAccountData = useCallback(
    (accountId?: number) => {
      const isInvalid = getIsAccountInvalid(accountId);
      const isInactive = getIsAccountInactive(accountId);
      let color: string | undefined;
      if (isInvalid) color = theme.colors.red;
      if (isInactive) color = theme.colors.darkYellow;
      return {
        isInvalidOrInactive: isInvalid || isInactive,
        isInvalid,
        isInactive,
        color,
      };
    },
    [getIsAccountInactive, getIsAccountInvalid]
  );

  return {
    getInvalidAccountData,
  };
};

interface useDeleteAccountProps {
  openMessage: OpenMessage;
  isIos?: boolean;
}
export const useDeleteAccount = ({
  openMessage,
  isIos,
}: useDeleteAccountProps) => {
  const { user } = useUser();
  const [deleteAccountModalVisible, setDeleteAccountModalVisible] =
    useState(false);
  const lastSendDeleteAccount = useRef<number | undefined>();

  const onPressDeleteAccount = () => {
    const now: any = new Date();
    if (
      lastSendDeleteAccount.current &&
      Math.abs(now - lastSendDeleteAccount.current) <= 60000
    ) {
      openMessage(
        `We have already sent an email to this address. Please wait ${Math.round(
          60 - (now - lastSendDeleteAccount.current) / 1000
        )} seconds before trying again`,
        "error"
      );
    } else {
      setDeleteAccountModalVisible(true);
    }
  };

  const onDeleteAccountSuccess = () => {
    lastSendDeleteAccount.current = new Date().getTime();
  };

  return {
    deleteAccountModalVisible,
    closeDeleteAccountModal: () => setDeleteAccountModalVisible(false),
    onPressDeleteAccount,
    showDeleteAccountButton:
      (user?.hasFeatureFlag(SHOW_DELETE_USER_BUTTON_FEATURE_FLAG) || isIos) &&
      !!user?.email,
    onDeleteAccountSuccess,
  };
};

interface useDeleteAccountModalProps {
  openMessage: OpenMessage;
  displayResultMessage: DisplayResultMessage;
  closeModal: () => void;
  onSuccess: () => void;
}
export const useDeleteAccountModal = ({
  openMessage,
  displayResultMessage,
  closeModal,
  onSuccess,
}: useDeleteAccountModalProps) => {
  const { user } = useUser();
  const [deleteCornixAccountConfirmationEmail, { isLoading }] =
    sharedLib.sharedApi.authentication.useDeleteCornixAccountConfirmationEmailMutation();

  const onDeleteAccount = async () => {
    if (user?.email) {
      displayResultMessage(
        deleteCornixAccountConfirmationEmail({
          email: user.email,
          request_source: RequestSource.APP,
        }).unwrap()
      ).then(() => {
        closeModal();
        onSuccess();
      });
    } else {
      openMessage(
        "Something went wrong. Please restart the app and try again.",
        "error"
      );
    }
  };

  return {
    onDeleteAccount,
    isLoading,
    content:
      "Deleting your account will also delete all associated data including subscription and trade information. This action is irreversible.",
  };
};
